/**
 * KyTestPage - Ky Test 통합 페이지
 *
 * 학생관리 페이지에서 Ky Test 버튼 클릭 시 이동하는 통합 페이지
 * - 상단: AI Tracker 콘텐츠 (AI/Engine/RawData 탭)
 * - 하단: 교육과정 관리 (Stamp 목록/새 교육과정 추가)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BypassLayout, Button } from '../components/common';
import { AITrackerContent } from '../components/AITrackerContent';
import { ArrowLeft, Sparkles, Loader2 } from 'lucide-react';
import StampSection from '../components/KyTestPage/StampSection';
import AITestModal from '../components/KyTestPage/AITestModal';
import ExtractionConfigModal from '../components/KyTestPage/ExtractionConfigModal';

function KyTestPage() {
  const { student_id } = useParams();
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();

  // 기본 상태 - 학생 정보는 genetics API에서 가져옴
  const [student, setStudent] = useState(null);
  const [isLoadingStudent, setIsLoadingStudent] = useState(false);
  const [aiTrackerTab, setAiTrackerTab] = useState('ai');

  // 스탬프 관련 상태
  const [stamps, setStamps] = useState([]);
  const [stampTab, setStampTab] = useState('list');

  // 문제 추출 관련 상태
  const [extractedProblems, setExtractedProblems] = useState([]);
  const [problemsByTree, setProblemsByTree] = useState({});
  const [isExtracting, setIsExtracting] = useState(false);
  const [engineData, setEngineData] = useState(null);
  const [geneticsData, setGeneticsData] = useState(null);

  // 모달 상태
  const [showAITestModal, setShowAITestModal] = useState(false);
  const [selectedStamp, setSelectedStamp] = useState(null);
  const [selectedTreeProblems, setSelectedTreeProblems] = useState([]);
  const [showExtractionConfig, setShowExtractionConfig] = useState(false);


  // 스탬프 목록 조회
  const fetchStamps = useCallback(async () => {
    if (!student_id) return;

    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams?student_id=${student_id}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.status === 204) {
        setStamps([]);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.data?.stamps) {
        setStamps(result.data.stamps);
      } else {
        setStamps([]);
      }
    } catch (error) {
      console.error('스탬프 목록 조회 실패:', error);
      setStamps([]);
    }
  }, [student_id, getAccessToken]);

  // 문제 추출 (get-pids + tree-info)
  const handleExtractProblems = useCallback(async ({ difficulty, userSelectedCids } = {}) => {
    if (!engineData || !student_id) {
      alert('AI Tracker 데이터를 먼저 로드해주세요.');
      return;
    }

    setIsExtracting(true);
    try {
      const token = await getAccessToken();

      // 1. get-pids API 호출
      const getPidsResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/genetics/get-pids`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            concept_index: engineData.concept_index,
            r_status: engineData.r_status,
            h_status: engineData.h_status,
            stability: engineData.stability,
            total_record: engineData.total_record,
            v_scale: engineData.v_scale,
            user_selected_cids: userSelectedCids || [],
            difficulty: difficulty || [1, 2, 3, 4, 5],
            problem_number: 200,
            solved_exception: true,
            student_id: parseInt(student_id)
          })
        }
      );

      if (!getPidsResponse.ok) {
        throw new Error('문제 추출 API 실패');
      }

      const getPidsResult = await getPidsResponse.json();
      const problems = getPidsResult.data?.problems || [];

      if (problems.length === 0) {
        alert('추출된 문제가 없습니다.');
        setExtractedProblems([]);
        setProblemsByTree({});
        return;
      }

      // 2. referencefields/by-pids API 호출 (200개씩 분할)
      const BATCH_SIZE = 200;
      const problemIds = problems.map(p => p.problem_id);
      const batches = [];
      for (let i = 0; i < problemIds.length; i += BATCH_SIZE) {
        batches.push(problemIds.slice(i, i + BATCH_SIZE));
      }

      const treeInfoResults = await Promise.all(
        batches.map(async (batch) => {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/referencefields/by-pids?problem_ids=${batch.join(',')}`,
            {
              headers: {
                'Authorization': token,
                'Content-Type': 'application/json',
              }
            }
          );
          if (!response.ok) {
            console.error('tree-info API 실패:', response.status);
            return [];
          }
          const result = await response.json();
          return result.data || [];
        })
      );

      const treeInfoList = treeInfoResults.flat();

      // 3. 데이터 병합 (한 문제가 여러 tree_id를 가질 수 있음)
      const treeInfoByPid = {};
      treeInfoList.forEach(item => {
        if (!treeInfoByPid[item.problem_id]) {
          treeInfoByPid[item.problem_id] = [];
        }
        treeInfoByPid[item.problem_id].push(item);
      });

      const mergedProblems = problems.map(p => ({
        ...p,
        treeInfos: treeInfoByPid[p.problem_id] || [],
      }));

      setExtractedProblems(mergedProblems);

      // 4. tree_id 기준 그룹화 (한 문제가 여러 tree에 속할 수 있음)
      const grouped = {};
      mergedProblems.forEach(p => {
        p.treeInfos.forEach(info => {
          if (info.tree_id !== null) {
            if (!grouped[info.tree_id]) {
              grouped[info.tree_id] = [];
            }
            grouped[info.tree_id].push({
              ...p,
              tree_id: info.tree_id,
              address: info.address || null,
              step: info.step || null,
            });
          }
        });
      });

      setProblemsByTree(grouped);




    } catch (error) {
      console.error('문제 추출 실패:', error);
      alert('문제 추출에 실패했습니다.');
    } finally {
      setIsExtracting(false);
    }
  }, [engineData, student_id, getAccessToken]);

  // AI 테스트 버튼 클릭 핸들러
  const handleAITestClick = useCallback((stamp) => {
    const treeProblems = problemsByTree[String(stamp.tree_id)] || [];
    setSelectedStamp(stamp);
    setSelectedTreeProblems(treeProblems);
    setShowAITestModal(true);
  }, [problemsByTree]);

  // 학생 정보 조회
  const fetchStudentInfo = useCallback(async () => {
    if (!student_id) return;
    try {
      setIsLoadingStudent(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/student/card?student_id=${student_id}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.status === 404) {
        console.error(`학생 ID ${student_id}를 찾을 수 없습니다.`);
        navigate(-1);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 'success' && result.data) {
        const d = result.data;
        setStudent({
          student_id: d.student_id,
          student_name: d.student_name,
          school_name: d.school_name,
          school_grade: d.student_grade || d.school_grade,
        });
      }
    } catch (error) {
      console.error('학생 정보 조회 실패:', error);
    } finally {
      setIsLoadingStudent(false);
    }
  }, [student_id, getAccessToken, navigate]);

  // 초기 데이터 로드
  useEffect(() => {
    fetchStudentInfo();
    fetchStamps();
  }, [fetchStudentInfo, fetchStamps]);

  // AITrackerContent에서 engineData를 받아오는 콜백
  const handleEngineDataLoad = useCallback((data) => {
    setEngineData(data);
  }, []);

  // AITrackerContent에서 geneticsData를 받아오는 콜백
  const handleGeneticsDataLoad = useCallback((data) => {
    setGeneticsData(data);
  }, []);

  return (
    <BypassLayout
      loading={isLoadingStudent}
      renderContent={({ isMobileView }) => (
        <div
          className={`flex flex-col overflow-hidden rounded-3xl bg-white shadow-lg ${
            isMobileView ? 'flex-1' : 'w-full'
          }`}
          style={isMobileView ? {
            width: 'calc(100% - 28.8px)',
            height: 'calc(100% - 28.8px)',
            margin: '14.4px'
          } : {
            height: '100%'
          }}
        >
          {/* 페이지 헤더 */}
          <div className="shrink-0 border-b">
            <div className={`w-full ${isMobileView ? 'px-3 py-2' : 'px-4 py-3'}`}>
              <div className="flex items-center justify-between gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/student')}
                  className="flex shrink-0 items-center justify-center gap-1"
                  style={{ width: isMobileView ? undefined : '130px' }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  {!isMobileView && '학생관리'}
                </Button>

                {/* 학생 정보 카드 - 중앙 */}
                <div className="flex min-w-0 flex-1 items-center gap-3 rounded-3xl border border-gray-200 bg-white px-3 py-1.5 shadow-sm">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm text-white font-bold">
                    {student?.student_id || parseInt(student_id)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900">{student?.student_name || '학생'}</p>
                    <p className="truncate text-xs text-gray-500">{student?.school_name} {student?.school_grade ? `${student.school_grade}학년` : ''}</p>
                  </div>
                  {geneticsData && (
                    <div className="flex shrink-0 gap-3 text-sm">
                      {engineData && (
                        <div className="text-center">
                          <p className="text-[10px] text-gray-500">테스트 수</p>
                          <p className="text-xs font-bold text-gray-900">{engineData.exam_counts}</p>
                        </div>
                      )}
                      <div className="text-center">
                        <p className="text-[10px] text-gray-500">총 풀이</p>
                        <p className="text-xs font-bold text-primary">{geneticsData.solved_problems}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] text-gray-500">고유 문제</p>
                        <p className="text-xs font-bold text-green-600">{geneticsData.total_problems}</p>
                      </div>
                    </div>
                  )}
                </div>

                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowExtractionConfig(true)}
                  disabled={isExtracting || !engineData}
                  className="flex shrink-0 items-center justify-center gap-2"
                  style={{ width: isMobileView ? undefined : '130px' }}
                >
                  {isExtracting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  {!isMobileView && 'AI 문제 추출'}
                </Button>
              </div>
            </div>
          </div>

          {/* AI Tracker 섹션 (60%) */}
          <section className="flex-[3] min-h-0 border-b overflow-auto">
            <div className={`w-full flex flex-col h-full ${isMobileView ? 'px-3 py-4' : 'px-4 py-4'}`}>
              {/* 통계 요약 바(exam/solved/unique/unlabeled/delta/정렬)는 카이테스트에서 숨김.
                  AITrackerContent 공유 컴포넌트를 손대지 않고 기존 hideEngineSummary 플래그로 이 페이지만 적용
                  (students-hub는 이미 동일 플래그로 숨김 → 영향 없음). */}
              <AITrackerContent
                studentId={parseInt(student_id)}
                studentInfo={student || { student_name: '학생', school_name: '', school_grade: '' }}
                activeTab={aiTrackerTab}
                onTabChange={setAiTrackerTab}
                onEngineDataLoad={handleEngineDataLoad}
                onGeneticsDataLoad={handleGeneticsDataLoad}
                hideStudentInfo
                hideEngineSummary
              />
            </div>
          </section>

          {/* 교육과정 관리 섹션 (40%) */}
          <section className="flex-[2] min-h-0 overflow-auto">
            <div className={`w-full ${isMobileView ? 'px-3 py-4' : 'px-4 py-4'}`}>
              <StampSection
                studentId={student_id}
                stamps={stamps}
                problemsByTree={problemsByTree}
                extractedCount={extractedProblems.length}
                activeTab={stampTab}
                onTabChange={setStampTab}
                onAITestClick={handleAITestClick}
                onStampsChange={fetchStamps}
              />
            </div>
          </section>

          {/* AI 문제 추출 설정 모달 */}
          {showExtractionConfig && (
            <ExtractionConfigModal
              isOpen={showExtractionConfig}
              onClose={() => setShowExtractionConfig(false)}
              onExtract={async ({ difficulty, userSelectedCids }) => {
                setShowExtractionConfig(false);
                await handleExtractProblems({ difficulty, userSelectedCids });
              }}
              isExtracting={isExtracting}
              engineData={engineData}
            />
          )}

          {/* AI 테스트 생성 모달 */}
          {showAITestModal && selectedStamp && (
            <AITestModal
              isOpen={showAITestModal}
              onClose={() => {
                setShowAITestModal(false);
                setSelectedStamp(null);
                setSelectedTreeProblems([]);
              }}
              studentId={student_id}
              studentName={student?.student_name}
              stamp={selectedStamp}
              problems={selectedTreeProblems}
              onTestCreated={fetchStamps}
            />
          )}
        </div>
      )}
    />
  );
}

export default KyTestPage;
